<template>
	<div class="xs-12">
		<div class="block">
			<!-- Label -->
			<label :for="name" class="xs-12 fw7 lh125" :class="fsLabel">
				{{ label }}
				<slot name="label"></slot>
			</label>
			<!-- Subheadline -->
			<span class="fs3 fw6 c6" v-if="subheadline">{{
				`(${subheadline})`
			}}</span>
		</div>

		<!-- Description -->
		<div class="xs-12 fs3 lh125" v-if="description">
			{{ description }}
			<slot name="description"></slot>
		</div>

		<!-- Component for input -->
		<component
			:is="componentName"
			:id="name"
			:name="name"
			:value="value"
			:options="options"
			:placeholder="placeholder"
			:size="size"
			:status="
				validationErrors && validationErrors.first(name) ? 'error' : status
			"
			v-bind="{ ...$props, ...$attrs }"
			v-on="$listeners"
		/>

		<!-- Error message -->
		<div
			class="xs-12 fs3 lh125 cError mt1"
			v-if="validationErrors && validationErrors.first(name)"
		>
			{{ validationErrors ? validationErrors.first(name) : null }}
		</div>
	</div>
</template>

<script>
/**
 * This component is a wrapper around all from inputs
 * Its main role is to standardise how we handle form inputs
 * Our design system is very granular for flexibility, but in time we have discoverd patterns
 * around forms that we can extract and make it easier for devs to create a form while keeping design consistent
 */
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputPassword from "@/components/JBaseInputPassword"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseInputNumberStepper from "@/components/JBaseInputNumberStepper"
import JBaseCheckboxGroup from "@/components/JBaseCheckboxGroup"
import JBaseDatePicker from "@/components/JBaseDatePicker"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"
import JInputDateOfBirth from "@/components/JInputDateOfBirth"
import JBaseMultiSelect from "@/components/JBaseMultiSelect"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import JBaseRadios from "@/components/JBaseRadios"
import JBaseRadiosWithDescription from "@/components/JBaseRadios"
import JBaseSelect from "@/components/JBaseSelect"
import JBaseToggle from "@/components/JBaseToggle"
import JInputList from "@/components/JInputList"
import JInputObjectList from "@/components/JInputObjectList"
import CoreSelectInsurer from "@/modules/core/components/CoreSelectInsurer"
import CoreSelectInternals from "@/modules/core/components/CoreSelectInternals"
import CoreSelectCurrency from "@/modules/core/components/CoreSelectCurrency"
import CoreSelectInsuranceType from "@/modules/core/components/CoreSelectInsuranceType"
import CoreSelectProduct from "@/modules/core/components/CoreSelectProduct"
import CoreSelectRole from "@/modules/core/components/CoreSelectRole"
import CoreSelectClient from "@/modules/core/components/CoreSelectClient"
import CoreSelectOrganizations from "@/modules/core/components/CoreSelectOrganizations"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"

export default {
	components: {
		JBaseInputText,
		JBaseInputPassword,
		JBaseInputLongText,
		JBaseInputNumber,
		JBaseInputNumberStepper,
		JBaseCheckboxGroup,
		JBaseDatePicker,
		JBaseDatePickerSimple,
		JInputDateOfBirth,
		JBaseMultiSelect,
		JBaseMultiSelectWithFlexibleValue,
		JBaseRadios,
		JBaseRadiosWithDescription,
		JBaseSelect,
		JBaseToggle,
		JInputList,
		JInputObjectList,
		CoreSelectInsurer,
		CoreSelectInternals,
		CoreSelectCurrency,
		CoreSelectInsuranceType,
		CoreSelectProduct,
		CoreSelectRole,
		CoreSelectClient,
		CoreSelectOrganizations,
		CoreMonacoEditor
	},

	props: {
		/**
		 * Important for validation and label, has to be unique in one form
		 */
		name: { type: String, required: true },
		/**
		 * e.g. j-base-input-text
		 */
		componentName: { type: String, required: true },
		value: {
			required: true
		},
		options: {
			type: Array,
			required: false
		},
		label: {
			type: String
		},
		subheadline: { type: String },
		placeholder: { type: String },
		description: { type: String },
		/**
		 * From v-validate
		 */
		validationErrors: {
			required: false
		},
		/**
		 * Size of input component xsmall, small, normal, large, xlarge, null
		 */
		size: {
			validator(val) {
				return val === null || typeof val === "string"
			}
		},
		/**
		 * Size of label
		 */
		fsLabel: {
			type: String,
			required: false,
			default: "fs4"
		},
		/**
		 * Status of input component success, error, warning, null
		 */
		status: {
			validator(val) {
				return val === null || typeof val === "string"
			}
		}
	}
}
</script>
